"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Loader, Leaf, ArrowRight, CircleCheck } from "lucide-react";
function InquiryFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="147716" className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
                <div className="size-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                    <CircleCheck className="size-8 text-primary" />
                </div>
                <h3 className="text-2xl font-serif font-bold text-foreground">Solicitação Enviada</h3>
                <p className="text-muted-foreground max-w-sm">{successMessage}</p>
                <Button onClick={resetForm} variant="outline" className="mt-6 rounded-full px-8 border-primary text-primary hover:bg-primary/5">
                    Enviar Outra Solicitação
                </Button>
            </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <FormField control={control} name="name" rules={{
        required: "Nome é obrigatório",
        minLength: {
          value: 2,
          message: "O nome deve ter pelo menos 2 caracteres"
        }
      }} render={({
        field
      }) => <FormItem className="col-span-1 md:col-span-2">
                            <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Nome Completo</FormLabel>
                            <FormControl>
                                <Input placeholder="Ex: João Silva" className="bg-background/50 border-border/50 focus-visible:ring-primary focus-visible:border-primary h-12 rounded-xl transition-all duration-300" {...field} />
                            </FormControl>
                            <FormDescription className="text-[10px]">Insira seu nome para identificação.</FormDescription>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="email" rules={{
        required: "E-mail é obrigatório",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Por favor, insira um e-mail válido"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">E-mail Corporativo</FormLabel>
                            <FormControl>
                                <Input type="email" placeholder="joao@empresa.com.br" className="bg-background/50 border-border/50 focus-visible:ring-primary focus-visible:border-primary h-12 rounded-xl transition-all duration-300" {...field} />
                            </FormControl>
                            <FormDescription className="text-[10px]">Usaremos este e-mail para o contato inicial.</FormDescription>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">WhatsApp / Telefone</FormLabel>
                            <FormControl>
                                <Input type="tel" placeholder="(11) 99999-9999" className="bg-background/50 border-border/50 focus-visible:ring-primary focus-visible:border-primary h-12 rounded-xl transition-all duration-300" {...field} />
                            </FormControl>
                            <FormDescription className="text-[10px]">Opcional – agiliza o atendimento.</FormDescription>
                            <FormMessage />
                        </FormItem>} />
            </div>

            <FormField control={control} name="project_description" rules={{
      required: "A descrição é necessária",
      minLength: {
        value: 10,
        message: "Por favor, forneça mais detalhes (mín. 10 caracteres)"
      }
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Sua Necessidade</FormLabel>
                        <FormControl>
                            <Textarea placeholder="Descreva seu desafio (ERP, Omnichannel, Marketplace, etc)..." className="bg-background/50 border-border/50 focus-visible:ring-primary focus-visible:border-primary min-h-[120px] rounded-xl resize-none transition-all duration-300" {...field} />
                        </FormControl>
                        <FormDescription className="text-[10px]">Inclua o tipo de solução Linx que você busca ou já utiliza.</FormDescription>
                        <FormMessage />
                    </FormItem>} />

            <FormField control={control} name="budget" render={({
      field
    }) => <FormItem>
                        <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Faturamento Mensal (R$)</FormLabel>
                        <FormControl>
                            <Input type="number" placeholder="Opcional" className="bg-background/50 border-border/50 focus-visible:ring-primary focus-visible:border-primary h-12 rounded-xl transition-all duration-300" {...field} />
                        </FormControl>
                        <FormDescription className="text-[10px]">Ajuda-nos a dimensionar a melhor solução para seu porte.</FormDescription>
                        <FormMessage />
                    </FormItem>} />

            {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-xl text-destructive text-sm flex items-start gap-3">
                    <span className="mt-0.5"></span>
                    <p>{submitError}</p>
                </div>}

            <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-lg font-bold rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/30 hover:shadow-primary/50 transition-all duration-300 group">
                {isSubmitting ? <>
                        <Loader className="animate-spin mr-2 size-5" />
                        Enviando Solicitação...
                    </> : <>
                        Solicitar Diagnóstico
                        <ArrowRight className="ml-2 size-5 group-hover:translate-x-1 transition-transform" />
                    </>}
            </Button>
        </Form>;
}
export default function Inquiry() {
  const {
    ref: sectionRef,
    isVisible: isSectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: formRef,
    isVisible: isFormVisible
  } = useRevealOnScroll({
    threshold: 0.2,
    once: true
  });
  return <section id="bottomcta" className="relative py-24 lg:py-32 bg-secondary text-secondary-foreground overflow-hidden" ref={sectionRef}>
            
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                <div className="absolute -top-[20%] -right-[10%] w-[70%] h-[70%] rounded-full bg-primary/10 blur-3xl opacity-50" />
                <div className="absolute -bottom-[20%] -left-[10%] w-[60%] h-[60%] rounded-full bg-accent/10 blur-3xl opacity-50" />
            </div>

            <div className="container mx-auto px-4 md:px-6 relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
                    
                    
                    <div className={`lg:col-span-5 flex flex-col justify-center transition-all duration-1000 ease-out ${isSectionVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/20 text-primary-foreground text-sm font-medium w-fit mb-6 backdrop-blur-md">
                            <Leaf className="size-4" />
                            <span>Transformação Digital</span>
                        </div>
                        
                        <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-secondary-foreground leading-tight tracking-wide mb-6">
                            Escalabilidade Real <br className="hidden lg:block" />
                            <span className="text-primary">para o seu Varejo</span> <br className="hidden lg:block" />
                            Omnichannel.
                        </h2>
                        
                        <p className="text-lg mb-8 max-w-md leading-relaxed !text-[hsl(0,0%,100%)]">A Linx Vila Velha é sua parceira estratégica na implementação das melhores soluções Linx. Agende um diagnóstico gratuito e descubra como otimizar sua gestão de ponta a ponta.</p>

                        <div className="flex items-center gap-6 text-sm font-medium text-secondary-foreground">
                            <div className="flex items-center gap-2">
                                <div className="size-10 rounded-full bg-primary/20 flex items-center justify-center text-primary">
                                    <CircleCheck className="size-5" />
                                </div>
                                <span>Especialistas Linx</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="size-10 rounded-full bg-primary/20 flex items-center justify-center text-primary">
                                    <CircleCheck className="size-5" />
                                </div>
                                <span>Suporte Próximo</span>
                            </div>
                        </div>
                    </div>

                    
                    <div ref={formRef} className={`lg:col-span-7 lg:pl-12 transition-all duration-1000 delay-200 ease-out ${isFormVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
                        <Card className="bg-card/80 backdrop-blur-xl border-border/40 shadow-2xl shadow-primary/10 rounded-3xl overflow-hidden relative">
                            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-accent to-primary opacity-70" />
                            
                            <CardHeader className="pb-6 pt-8 px-8">
                                <CardTitle className="text-2xl font-serif font-bold text-foreground">Diagnóstico Especializado</CardTitle>
                                <CardDescription className="text-base text-muted-foreground">
                                    Preencha o formulário abaixo e nossa equipe entrará em contato para agendar sua consultoria.
                                </CardDescription>
                            </CardHeader>
                            
                            <CardContent className="px-8 pb-8">
                                <FormProvider formKey="bottom_cta_microvix" formId="bottom_cta_microvix" sectionName="Inquiry" defaultValues={{
                name: "",
                email: "",
                phone: "",
                project_description: "",
                budget: ""
              }} validationRules={{
                name: {
                  required: true,
                  minLength: 2
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                project_description: {
                  required: true,
                  minLength: 10
                }
              }} submitText="Solicitar Diagnóstico" successMessage="Obrigado! Recebemos sua solicitação de diagnóstico e entraremos em contato em breve para agendar sua consultoria.">
                                    <InquiryFormInner />
                                </FormProvider>
                            </CardContent>
                        </Card>
                    </div>

                </div>
            </div>
        </section>;
}